import * as THREE from 'three'
import type { ResolvedTrackRegionConfig } from './trackRegions'

// JSON Types
type Vec3Array = [number, number, number]
type QuatArray = [number, number, number, number]
type JsonSegment = {
    p0: Vec3Array
    h0: Vec3Array
    h1: Vec3Array
    p1: Vec3Array
    q0: QuatArray
    q1: QuatArray
}
export type TrackPathJson = {
    segments: JsonSegment[]
}

// Path Types
type BezierSegment = {
    p0: THREE.Vector3
    h0: THREE.Vector3
    h1: THREE.Vector3
    p1: THREE.Vector3
    q0: THREE.Quaternion
    q1: THREE.Quaternion
}
type LengthSample = {
    distance: number
    segmentIndex: number
    t: number
}

const arcLengthSamplesPerSegment = 32

export type TrackPath = {
    segments: BezierSegment[]
    lengthSamples: LengthSample[]
    totalLength: number
    pathEndDistance: number
    resolvedRegionConfig?: ResolvedTrackRegionConfig
}

export function parseTrackPathJson(json: TrackPathJson): TrackPath {
    const segments = json.segments.map((segment) => ({
        p0: new THREE.Vector3().fromArray(segment.p0),
        h0: new THREE.Vector3().fromArray(segment.h0),
        h1: new THREE.Vector3().fromArray(segment.h1),
        p1: new THREE.Vector3().fromArray(segment.p1),
        q0: new THREE.Quaternion().fromArray(segment.q0).normalize(),
        q1: new THREE.Quaternion().fromArray(segment.q1).normalize(),
    }))
    const lengthSamples = buildLengthSamples(segments)
    const totalLength = lengthSamples[lengthSamples.length - 1]?.distance ?? 0
    if (totalLength <= 0) {
        throw new Error('track-path.json produced a zero-length path.')
    }
    return {
        segments,
        lengthSamples,
        totalLength,
        pathEndDistance: totalLength,
    }
}

function evaluateBezier(segment: BezierSegment, t: number): THREE.Vector3 {
    const inv = 1 - t
    return new THREE.Vector3()
        .addScaledVector(segment.p0, inv * inv * inv)
        .addScaledVector(segment.h0, 3 * inv * inv * t)
        .addScaledVector(segment.h1, 3 * inv * t * t)
        .addScaledVector(segment.p1, t * t * t)
}

function buildLengthSamples(segments: BezierSegment[]): LengthSample[] {
    const samples: LengthSample[] = []
    let distance = 0
    let previous = segments[0].p0.clone()
    segments.forEach((segment, segmentIndex) => {
        const start = evaluateBezier(segment, 0)
        if (segmentIndex > 0) {
            distance += previous.distanceTo(start)
            previous = start
        }
        samples.push({ distance, segmentIndex, t: 0 })
        for (let step = 1; step <= arcLengthSamplesPerSegment; step += 1) {
            const t = step / arcLengthSamplesPerSegment
            const point = evaluateBezier(segment, t)
            distance += previous.distanceTo(point)
            samples.push({ distance, segmentIndex, t })
            previous = point
        }
    })
    return samples
}
